import { isDefined } from "@norma-bi/utils";
import { ErrorContext, ResponseError } from "./gen";

// These errors are thrown when a request returns a not OK (200-299) response
export class NotOKResponseError extends Error {
  response: Response;

  constructor(response: Response) {
    super("not-ok-response");
    this.response = response;
  }
}

// These errors are thrown when an API method has failed (without getting a response)
export class GeneralAPIError extends Error {
  context: ErrorContext;
  constructor(context: ErrorContext) {
    super("general-api-error");
    this.context = context;
  }
}

export type APIError = NotOKResponseError | GeneralAPIError | ResponseError;
export function isAPIError(e: unknown): e is APIError {
  return (
    e instanceof NotOKResponseError || e instanceof GeneralAPIError || e instanceof ResponseError
  );
}

export function isHTTPErrorWithStatusCodes(
  error: unknown,
  statusCodes: number[] | { from: number; to?: number },
) {
  if (!error) {
    return false;
  }

  if (error instanceof ResponseError || error instanceof NotOKResponseError) {
    const status = error.response?.status;
    if ("from" in statusCodes) {
      if (status < statusCodes.from) {
        return false;
      }
      if (isDefined(statusCodes.to)) {
        return status <= statusCodes.to;
      }
      return true;
    }
    return statusCodes.includes(status);
  }

  if (error instanceof GeneralAPIError) {
    return isHTTPErrorWithStatusCodes(error.context, statusCodes);
  }
}

export function isServerError(error: unknown) {
  return isHTTPErrorWithStatusCodes(error, { from: 500 });
}

export function isHTTPUnauthorized(error: unknown) {
  return isHTTPErrorWithStatusCodes(error, [401]);
}
